import type { FeatureFlag } from '@/shared/contexts/featureFlagsContext'
import { SubscriptionPlanType } from '@/types/subscription'

/**
 * Feature Flags Configuration
 *
 * This file defines all feature flags in the application. Each feature flag can be:
 * - Globally enabled/disabled via the `enabled` property
 * - Restricted to specific plans via the `requiredSubscriptions` property
 *
 * Usage Examples:
 *
 * 1. Basic feature flag (available to all plans when enabled):
 *    { name: 'playbookRenaming', enabled: true }
 *
 * 2. Plan-restricted feature (only available to Pro and Team plans):
 *    { name: 'playbookSharing', enabled: true, requiredSubscriptions: ['pro', 'team'] }
 *
 * 3. Globally disabled feature:
 *    { name: 'experimentalFeature', enabled: false }
 */

export const featureFlags = [
  // Settings Pages
  { name: 'settingsWorkspace', enabled: true },
  { name: 'settingsWorkspacePeopleManagement', enabled: false },
  { name: 'settingsNotificationsManagement', enabled: false },
  { name: 'settingsWorkspacePlans', enabled: true },
  // Plans
  { name: 'plansExplorerPlan', enabled: true },
  { name: 'plansProPlan', enabled: true },
  { name: 'plansTeamsPlan', enabled: false },
  // Playbook
  {
    name: 'playbookVersionControl',
    enabled: false,
    requiredSubscriptions: [
      SubscriptionPlanType.Pro,
      SubscriptionPlanType.Team,
    ],
  },
  {
    name: 'playbookCollaboration',
    enabled: false,
    requiredSubscriptions: [
      SubscriptionPlanType.Pro,
      SubscriptionPlanType.Team,
    ],
  },
  {
    name: 'playbookSharing',
    enabled: true,
    requiredSubscriptions: [
      SubscriptionPlanType.Pro,
      SubscriptionPlanType.Team,
    ],
  },
  {
    name: 'playbookNotes',
    enabled: false,
    requiredSubscriptions: [
      SubscriptionPlanType.Pro,
      SubscriptionPlanType.Team,
    ],
  },
  {
    name: 'playbookRenaming',
    enabled: true,
    requiredSubscriptions: [
      SubscriptionPlanType.Pro,
      SubscriptionPlanType.Team,
    ],
  },
  { name: 'playbookDuplication', enabled: true },
  { name: 'playbookPromptTesting', enabled: true },
  { name: 'playbookPromptEditing', enabled: true },
  { name: 'playbookVisibilityManagement', enabled: true },
  {
    name: 'playbookPrivateVisibility',
    enabled: true,
    requiredSubscriptions: [
      SubscriptionPlanType.Pro,
      SubscriptionPlanType.Team,
    ],
  },
  { name: 'playbookDeletion', enabled: true },
  {
    name: 'playbookDownload',
    enabled: false,
    requiredSubscriptions: [
      SubscriptionPlanType.Pro,
      SubscriptionPlanType.Team,
    ],
  },
  { name: 'playbookCategorization', enabled: true },
  { name: 'playbookFeedbacks', enabled: true },
  // Chat
  { name: 'chatFileUpload', enabled: false },
  // Workspaces
  { name: 'workspaceSwitch', enabled: true },
  { name: 'workspaceCreation', enabled: false },
  { name: 'workspaceLeave', enabled: false },
  { name: 'workspaceAvatar', enabled: false },
  // Recommendations
  { name: 'learningRecommendationsPage', enabled: false },
] as const satisfies readonly FeatureFlag[]

export type FeatureFlagName = (typeof featureFlags)[number]['name']
